import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Clock } from "lucide-react";

/**
 * Fase 13/14 · Auto-logout por inactividad (7 min) con PERSISTENCIA.
 *
 * Fix Fase 14: el último `last_activity` se guarda en localStorage. Al montar
 * (que ocurre en cada refresh porque el watcher vive dentro del DashboardLayout
 * autenticado), si `now - last_activity > 7 min` → logout INMEDIATO antes de
 * renderizar contenido autenticado. Esto funciona en TODOS los entornos (preview
 * y producción) sin depender de APP_ENV.
 *
 * - Timer principal: 7 min sin actividad → logout + flash.
 * - Aviso previo: a los 6 min se abre un diálogo modal con botón "Seguir conectado".
 * - Actividad rastreada: mousemove/mousedown/keydown/touchstart/scroll (throttled).
 */
const INACTIVITY_MS = 7 * 60 * 1000;       // 7 min
const WARNING_MS = 6 * 60 * 1000;          // aviso a los 6 min
const THROTTLE_MS = 5 * 1000;              // throttle de eventos
const STORAGE_KEY = "fstudy_last_activity";

const readStamp = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  const n = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) ? n : null;
};

const writeStamp = (ts) => {
  try { localStorage.setItem(STORAGE_KEY, String(ts)); } catch (_) { /* ignore */ }
};

const clearStamp = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
};

const forceInactivityLogout = async (logout) => {
  clearStamp();
  localStorage.setItem(
    "fstudy_login_flash",
    "FStudy: Sesión cerrada por inactividad."
  );
  try { await logout(); } catch (_) { /* ignore */ }
  window.location.href = "/login";
};

export default function InactivityWatcher() {
  const { logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (logoutTimerRef.current) { clearTimeout(logoutTimerRef.current); logoutTimerRef.current = null; }
  }, []);

  const scheduleTimers = useCallback(() => {
    clearTimers();
    warnTimerRef.current = setTimeout(() => setShowWarning(true), WARNING_MS);
    logoutTimerRef.current = setTimeout(async () => {
      setShowWarning(false);
      await forceInactivityLogout(logout);
    }, INACTIVITY_MS);
  }, [clearTimers, logout]);

  const resetActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityRef.current < THROTTLE_MS) return; // throttle
    lastActivityRef.current = now;
    writeStamp(now);   // Fase 14 · persistir para sobrevivir refresh
    if (showWarning) return; // si estamos en warning, solo "Seguir conectado" lo cierra
    scheduleTimers();
  }, [scheduleTimers, showWarning]);

  const keepAlive = useCallback(() => {
    setShowWarning(false);
    const now = Date.now();
    lastActivityRef.current = now;
    writeStamp(now);
    scheduleTimers();
  }, [scheduleTimers]);

  useEffect(() => {
    // Fase 14 · Al montar (post-hidratación), si el stamp está expirado forzamos
    // logout (defensa extra al chequeo del AuthContext). Si no, sembramos.
    const stamp = readStamp();
    const now = Date.now();
    if (stamp !== null && now - stamp > INACTIVITY_MS) {
      forceInactivityLogout(logout);
      return;
    }
    if (stamp === null) {
      writeStamp(now);
    }
    scheduleTimers();
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, resetActivity, { passive: true }));
    return () => {
      clearTimers();
      events.forEach((e) => window.removeEventListener(e, resetActivity));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open={showWarning} onOpenChange={(o) => { if (!o) keepAlive(); }}>
      <DialogContent className="sm:max-w-md" data-testid="inactivity-warning-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-full bg-brand-gold/15 flex items-center justify-center">
              <Clock className="h-5 w-5 text-brand-gold-dark" />
            </div>
            <DialogTitle className="font-serif-display text-2xl text-brand-ink">
              ¿Sigues ahí?
            </DialogTitle>
          </div>
          <DialogDescription className="text-stone-600">
            Tu sesión se cerrará en <span className="font-semibold text-brand-ink">1 minuto</span> por inactividad.
            Presiona el botón para continuar trabajando.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            onClick={keepAlive}
            className="btn-primary w-full sm:w-auto"
            data-testid="inactivity-stay-connected"
          >
            Seguir conectado
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
